import { EditPolygon, EditContent } from "@/pages/editor"
import {
	Box,
	Button,
	Checkbox,
	Flex,
	Heading,
	IconButton,
	Input,
	InputGroup,
	InputLeftAddon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	Select,
	Text,
	Textarea,
	Slider,
	SliderTrack,
	SliderFilledTrack,
	SliderThumb,
	Tooltip,
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	HStack,
	Badge,
	VStack,
	useColorModeValue,
	Kbd,
	SimpleGrid,
	Center,
	Alert,
	AlertIcon,
	FormControl,
	FormLabel
} from "@chakra-ui/react"
import { HotSpot, NewContentItem, PixelCoordinate, Polygon } from "@xnav/types/src/common"
import ListPropertyEditor, { ListItemComponentsProps } from "./listPropertyEditor"
import { useMedia, useSelectionReference, useSelectedContent } from "@/hooks/contextHooks"
import { MediaMeta, MediaType } from '@/pages/mediaManager'
import React, { useMemo, useState, useEffect } from "react"
import { ReactStateType } from "@xnav/types/src/frontend"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faAdd,
	faPlus,
	faExpand,
	faCompress,
	faInfoCircle,
	faTrash,
	faDrawPolygon,
	faMapPin,
	faFont,
	faClock,
	faPalette,
	faArrowsUpDownLeftRight,
	faVolumeHigh,
	faCheck,
	faScissors,
	faChevronRight,
	faChevronLeft,
	faLayerGroup,
	faPlay,
	faTimes,
	faRotateLeft,
} from "@fortawesome/free-solid-svg-icons"
import { generateMidpoints, getCircleEllipseParams, generateCircleEllipsePoints } from "@/functions/polygonFunctions"
import { MarkupTool } from "./videoMarkupEditor"

export type MarkupItem = Polygon | HotSpot

const AddButton: React.FC<{ label: string, onAdd: () => void }> = ({ label, onAdd }) => {
	return (
		<Button
			leftIcon={<FontAwesomeIcon icon={faAdd} />}
			onClick={onAdd}
			size="sm"
			w='100%'
			borderRadius="lg"
			variant="outline"
			colorScheme="blue"
		>
			Add New {label}
		</Button>
	)
}

const HiddenFrameInput = ({ value, onUpdate }: ListItemComponentsProps<string | number>) => (
	<Input
		size="sm"
		borderRadius="lg"
		bg={useColorModeValue("white", "gray.850")}
		border="1px solid"
		borderColor={useColorModeValue("gray.200", "gray.750")}
		_focus={{ borderColor: "blue.500", bg: useColorModeValue("white", "gray.900") }}
		placeholder="e.g. 5.3 or 6-8"
		onChange={(e) => onUpdate(e.target.value)}
		value={value}
		flex={1}
	/>
)

const CoordinateInput = ({ value, onUpdate }: ListItemComponentsProps<PixelCoordinate>) => (
	<Flex flexDir='column' justifyItems='stretch' flex={1} gap={1}>
		<InputGroup size="sm">
			<InputLeftAddon borderLeftRadius="lg">X:</InputLeftAddon>
			<Input
				type='number'
				borderRightRadius="lg"
				value={value.x !== undefined && !isNaN(value.x) ? Math.round(value.x) : ''}
				onChange={e => onUpdate({ x: Math.round(Number(e.target.value)), y: value.y })}
			/>
		</InputGroup>
		<InputGroup size="sm">
			<InputLeftAddon borderLeftRadius="lg">Y:</InputLeftAddon>
			<Input
				type='number'
				borderRightRadius="lg"
				value={value.y !== undefined && !isNaN(value.y) ? Math.round(value.y) : ''}
				onChange={e => onUpdate({ x: value.x, y: Math.round(Number(e.target.value)) })}
			/>
		</InputGroup>
	</Flex>
)

const AudioFileItem = ({ value, onDelete }: ListItemComponentsProps<any>) => {
	const [media] = useMedia() as [MediaMeta[] | undefined, any];
	const audioMedia = media?.find(m => m.id === value.id);

	const handlePlayAudio = () => {
		if (audioMedia?.fileUrl) {
			const sound = new Audio(audioMedia.fileUrl);
			sound.play();
		}
	};

	return (
		<Flex
			align='center'
			justify="space-between"
			p={2.5}
			borderRadius='xl'
			bg={useColorModeValue("white", "gray.800")}
			border="1px solid"
			borderColor={useColorModeValue("gray.200", "gray.700")}
			shadow="xs"
			w='100%'
		>
			<HStack spacing={2} minW={0} flex={1}>
				<IconButton
					aria-label="Play Audio"
					icon={<FontAwesomeIcon icon={faVolumeHigh} />}
					size="xs"
					colorScheme="green"
					variant="ghost"
					borderRadius="full"
					onClick={handlePlayAudio}
				/>
				<Text fontSize="xs" fontWeight="600" noOfLines={1} color={useColorModeValue("gray.700", "gray.200")}>
					{value.name}
				</Text>
			</HStack>
			<IconButton
				aria-label="Remove audio"
				icon={<FontAwesomeIcon icon={faTrash} />}
				size="xs"
				variant="ghost"
				colorScheme="red"
				onClick={() => onDelete?.()}
			/>
		</Flex>
	);
};

const PRESET_COLORS = [
	{ label: 'Emerald Green', hex: '#10b981' },
	{ label: 'Sky Blue', hex: '#0ea5e9' },
	{ label: 'Royal Blue', hex: '#3b82f6' },
	{ label: 'Indigo', hex: '#6366f1' },
	{ label: 'Rose Pink', hex: '#f43f5e' },
	{ label: 'Fuchsia', hex: '#d946ef' },
	{ label: 'Amber Gold', hex: '#f59e0b' },
	{ label: 'Coral Red', hex: '#ef4444' },
];

const MarkupPropertyEditor: React.FC<{
	markupItem?: MarkupItem,
	time: number,
	setMarkupItem: (markupItem: MarkupItem) => void
	onDone: () => void
	onDelete?: () => void
	isExpanded?: boolean
	onToggleExpand?: () => void
	isCollapsed?: boolean
	onToggleCollapse?: () => void
	onSelectMarkup?: (type: 'polygon' | 'hotspot', index: number) => void
	onSeekTime?: (time: number) => void
	tool?: MarkupTool
	setTool?: React.Dispatch<React.SetStateAction<MarkupTool>>
	onDeleteMarkup?: (type: 'polygon' | 'hotspot', index: number) => void
	isTrimming?: boolean
	setIsTrimming?: (val: boolean) => void
	videoDuration?: number
}> = ({ markupItem, time, setMarkupItem, onDone, onDelete, isExpanded, onToggleExpand, isCollapsed, onToggleCollapse, onSelectMarkup, onSeekTime, tool, setTool, onDeleteMarkup, isTrimming, setIsTrimming, videoDuration }) => {
	const [selectionReference] = useSelectionReference() as ReactStateType<NewContentItem | undefined>
	const [, setMedia] = useMedia() as ReactStateType<MediaMeta[]>
	const [media] = useMedia() as ReactStateType<MediaMeta[]>
	const [content, setContent] = useSelectedContent() as ReactStateType<EditContent>

	const allAudios = useMemo(() => {
		if (!media) return []
		return media.filter(m => m.type === MediaType.Audio)
	}, [media])

	const cardBg = useColorModeValue("white", "gray.850");
	const cardBorder = useColorModeValue("gray.200", "gray.750");
	const labelColor = useColorModeValue("gray.600", "gray.400");
	const sectionTitleColor = useColorModeValue("gray.700", "gray.300");

	const [trimIn, setTrimIn] = useState<number>(content?.clipStart ?? 0);
	const [trimOut, setTrimOut] = useState<number>(content?.clipEnd ?? (videoDuration || 10));
	const [trimInStr, setTrimInStr] = useState<string>((content?.clipStart ?? 0).toFixed(1));
	const [trimOutStr, setTrimOutStr] = useState<string>((content?.clipEnd ?? (videoDuration || 10)).toFixed(1));

	useEffect(() => {
		const curStart = content?.clipStart ?? 0;
		let curEnd = content?.clipEnd;
		if (curEnd === undefined || curEnd <= curStart) {
			curEnd = (videoDuration && videoDuration > curStart) ? videoDuration : curStart + 10;
		}
		setTrimIn(curStart);
		setTrimOut(curEnd);
		setTrimInStr(curStart.toFixed(1));
		setTrimOutStr(curEnd.toFixed(1));
	}, [content?.clipStart, content?.clipEnd, videoDuration, isTrimming]);

	function parseInputSec(val: string): number | null {
		if (!val || !val.trim()) return null;
		if (val.includes(':')) {
			const parts = val.split(':');
			const m = parseFloat(parts[0]) || 0;
			const s = parseFloat(parts[1]) || 0;
			return Math.max(0, Math.round((m * 60 + s) * 10) / 10);
		}
		const num = parseFloat(val);
		return isNaN(num) ? null : Math.max(0, Math.round(num * 10) / 10);
	}

	const handleInChange = (val: string) => {
		setTrimInStr(val);
		const parsed = parseInputSec(val);
		if (parsed !== null) setTrimIn(parsed);
	};

	const handleOutChange = (val: string) => {
		setTrimOutStr(val);
		const parsed = parseInputSec(val);
		if (parsed !== null) setTrimOut(parsed);
	};

	const stepIn = (delta: number) => {
		const next = Math.max(0, Math.round((trimIn + delta) * 10) / 10);
		setTrimIn(next);
		setTrimInStr(next.toFixed(1));
	};

	const stepOut = (delta: number) => {
		const next = Math.max(0, Math.round((trimOut + delta) * 10) / 10);
		setTrimOut(next);
		setTrimOutStr(next.toFixed(1));
	};

	const handleApplyTrim = () => {
		setContent({
			...content,
			clipStart: trimIn,
			clipEnd: trimOut
		});
		if (time < trimIn || time >= trimOut) {
			onSeekTime?.(trimIn);
		}
		setIsTrimming?.(false);
	};

	const handleResetTrim = () => {
		setContent({
			...content,
			clipStart: undefined,
			clipEnd: undefined
		});
		setIsTrimming?.(false);
	};

	const hasValidClip = Boolean(
		content?.clipStart !== undefined &&
		content?.clipEnd !== undefined &&
		content.clipEnd > content.clipStart
	);

	if (isCollapsed) {
		return (
			<Flex 
				align="center" 
				justify="flex-start" 
				flexDir="column" 
				w="42px" 
				h="full" 
				bg={useColorModeValue("gray.50", "gray.900")} 
				borderLeft="1px solid" 
				borderColor={useColorModeValue("gray.200", "gray.800")}
				py={4}
				gap={4}
			>
				<Tooltip label="Expand Inspector" placement="left" hasArrow>
					<IconButton 
						aria-label="Expand Inspector" 
						icon={<FontAwesomeIcon icon={faChevronLeft} />} 
						size="sm" 
						variant="ghost" 
						borderRadius="full"
						onClick={onToggleCollapse} 
					/>
				</Tooltip>
				<Tooltip label={`${(content?.polygons?.length || 0) + (content?.hotSpots?.length || 0)} Tags Total`} placement="left" hasArrow>
					<Badge 
						colorScheme="green" 
						variant="solid" 
						borderRadius="full" 
						px={2} 
						py={1} 
						fontSize="xs"
						cursor="pointer"
						onClick={onToggleCollapse}
					>
						{(content?.polygons?.length || 0) + (content?.hotSpots?.length || 0)}
					</Badge>
				</Tooltip>
			</Flex>
		);
	}

	if (!markupItem && isTrimming) {
		return (
			<Flex 
				flexDir="column" 
				w="full" 
				h="full" 
				bg={useColorModeValue("white", "gray.900")} 
				borderLeft="1px solid" 
				borderColor={useColorModeValue("gray.200", "gray.800")}
			>
				{/* Modern Header */}
				<Flex 
					justify="space-between" 
					align="center" 
					px={4} 
					py={3} 
					borderBottom="1px solid" 
					borderColor={cardBorder} 
					bg={cardBg}
				>
					<HStack spacing={2.5}>
						<Flex 
							w="28px" 
							h="28px" 
							borderRadius="lg" 
							bg="teal.50" 
							_dark={{ bg: "teal.900" }} 
							align="center" 
							justify="center" 
							color="teal.500" 
							boxShadow="sm"
						>
							<FontAwesomeIcon icon={faScissors} style={{ fontSize: "13px" }} />
						</Flex>
						<Box>
							<Heading size="xs" textTransform="uppercase" letterSpacing="wider">Trim Chapter</Heading>
							<Text fontSize="10px" color="gray.500">Video boundaries for this chapter</Text>
						</Box>
					</HStack>
					<HStack spacing={1}>
						<Badge colorScheme={hasValidClip ? "teal" : "gray"} variant="subtle" borderRadius="full" px={2} py={0.5} fontSize="2xs">
							{hasValidClip ? "Active Trim" : "Full Video"}
						</Badge>
						<Tooltip label="Close Trimmer" placement="top" hasArrow>
							<IconButton 
								aria-label="Close trimmer" 
								size="xs" 
								variant="ghost" 
								borderRadius="full"
								icon={<FontAwesomeIcon icon={faTimes} />} 
								onClick={() => setIsTrimming?.(false)} 
							/>
						</Tooltip>
					</HStack>
				</Flex>

				{/* Scrollable Trimmer Body */}
				<Box flex={1} overflowY="auto" p={3.5}>
					<VStack spacing={3.5} align="stretch">
						{/* Visual Master Video Range Track */}
						<Box 
							p={3} 
							borderRadius="xl" 
							bg={useColorModeValue("gray.50", "gray.850")} 
							border="1px solid" 
							borderColor={cardBorder}
							boxShadow="xs"
						>
							<Flex justify="space-between" align="center" fontSize="2xs" color="gray.500" mb={2} fontWeight="bold">
								<Text>0:00 (Start)</Text>
								<Badge 
									colorScheme={trimOut > trimIn ? "teal" : "red"} 
									variant="solid" 
									borderRadius="full" 
									px={2} 
									py={0.5} 
									fontSize="2xs"
								>
									{trimOut > trimIn ? `Length: ${(trimOut - trimIn).toFixed(1)}s` : "Invalid Bounds"}
								</Badge>
								<Text>{(videoDuration || 0).toFixed(1)}s (Total)</Text>
							</Flex>
							{/* Track Bar */}
							<Box 
								pos="relative" 
								h="10px" 
								bg={useColorModeValue("gray.200", "gray.700")} 
								borderRadius="full" 
								overflow="hidden"
							>
								{(videoDuration || 0) > 0 && (
									<Box
										pos="absolute"
										left={`${Math.max(0, Math.min(100, (trimIn / (videoDuration || 1)) * 100))}%`}
										width={`${Math.max(0, Math.min(100, ((trimOut - trimIn) / (videoDuration || 1)) * 100))}%`}
										top={0}
										bottom={0}
										bgGradient="linear(to-r, teal.400, cyan.400)"
										borderRadius="full"
										boxShadow="0 0 8px rgba(20, 184, 166, 0.4)"
									/>
								)}
							</Box>
						</Box>

						{/* Start Time (In-Point) Box */}
						<Box 
							p={3} 
							borderRadius="xl" 
							bg={cardBg} 
							border="1px solid" 
							borderColor={cardBorder}
							boxShadow="xs"
						>
							<Flex justify="space-between" align="center" mb={2}>
								<Text fontSize="10px" fontWeight="bold" color="teal.500" textTransform="uppercase" letterSpacing="wider">
									Start Time (In-Point)
								</Text>
								<Button
									size="2xs"
									variant="ghost"
									colorScheme="teal"
									borderRadius="full"
									leftIcon={<FontAwesomeIcon icon={faPlay} style={{ fontSize: "9px" }} />}
									onClick={() => onSeekTime?.(trimIn)}
								>
									Preview
								</Button>
							</Flex>
							<HStack spacing={1.5}>
								<Input
									size="sm"
									borderRadius="lg"
									value={trimInStr}
									onChange={(e) => handleInChange(e.target.value)}
									placeholder="0.0 or 0:00"
									fontFamily="mono"
									fontSize="xs"
									fontWeight="semibold"
									textAlign="center"
									bg={useColorModeValue("white", "gray.850")}
									borderColor={cardBorder}
									_focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px #14b8a6" }}
								/>
								<IconButton
									aria-label="-1s"
									size="sm"
									borderRadius="md"
									variant="outline"
									icon={<Text fontSize="2xs" fontWeight="bold">-1s</Text>}
									onClick={() => stepIn(-1)}
								/>
								<IconButton
									aria-label="+1s"
									size="sm"
									borderRadius="md"
									variant="outline"
									icon={<Text fontSize="2xs" fontWeight="bold">+1s</Text>}
									onClick={() => stepIn(1)}
								/>
							</HStack>
						</Box>

						{/* End Time (Out-Point) Box */}
						<Box 
							p={3} 
							borderRadius="xl" 
							bg={cardBg} 
							border="1px solid" 
							borderColor={cardBorder}
							boxShadow="xs"
						>
							<Flex justify="space-between" align="center" mb={2}>
								<Text fontSize="10px" fontWeight="bold" color="teal.500" textTransform="uppercase" letterSpacing="wider">
									End Time (Out-Point)
								</Text>
								<Button
									size="2xs"
									variant="ghost"
									colorScheme="teal"
									borderRadius="full"
									leftIcon={<FontAwesomeIcon icon={faPlay} style={{ fontSize: "9px" }} />}
									onClick={() => onSeekTime?.(trimOut)}
								>
									Preview
								</Button>
							</Flex>
							<HStack spacing={1.5}>
								<Input
									size="sm"
									borderRadius="lg"
									value={trimOutStr}
									onChange={(e) => handleOutChange(e.target.value)}
									placeholder="20.0 or 0:20"
									fontFamily="mono"
									fontSize="xs"
									fontWeight="semibold"
									textAlign="center"
									bg={useColorModeValue("white", "gray.850")}
									borderColor={cardBorder}
									_focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px #14b8a6" }}
								/>
								<IconButton
									aria-label="-1s"
									size="sm"
									borderRadius="md"
									variant="outline"
									icon={<Text fontSize="2xs" fontWeight="bold">-1s</Text>}
									onClick={() => stepOut(-1)}
								/>
								<IconButton
									aria-label="+1s"
									size="sm"
									borderRadius="md"
									variant="outline"
									icon={<Text fontSize="2xs" fontWeight="bold">+1s</Text>}
									onClick={() => stepOut(1)}
								/>
							</HStack>
						</Box>

						{/* Validation warning */}
						{trimOut <= trimIn && (
							<Alert status="warning" borderRadius="xl" py={2} px={3} fontSize="xs">
								<AlertIcon boxSize="14px" />
								End Time ({trimOut.toFixed(1)}s) must be greater than Start Time ({trimIn.toFixed(1)}s).
							</Alert>
						)}
					</VStack>
				</Box>

				{/* Footer Actions */}
				<Box p={3} borderTop="1px solid" borderColor={cardBorder} bg={cardBg}>
					<VStack spacing={2} w="full">
						<Button
							w="full"
							size="sm"
							colorScheme="teal"
							borderRadius="xl"
							leftIcon={<FontAwesomeIcon icon={faCheck} />}
							isDisabled={trimOut <= trimIn}
							onClick={handleApplyTrim}
							boxShadow="0 4px 14px rgba(20, 184, 166, 0.35)"
							_hover={{ transform: "translateY(-1px)", boxShadow: "0 6px 20px rgba(20, 184, 166, 0.45)" }}
							transition="all 0.15s ease"
						>
							Apply Trim
						</Button>
						<HStack spacing={2} w="full">
							{(content?.clipStart !== undefined || content?.clipEnd !== undefined) && (
								<Button
									flex={1}
									size="xs"
									variant="ghost"
									colorScheme="red"
									borderRadius="lg"
									leftIcon={<FontAwesomeIcon icon={faRotateLeft} />}
									onClick={handleResetTrim}
								>
									Reset Full Video
								</Button>
							)}
							<Button
								flex={1}
								size="xs"
								variant="outline"
								borderRadius="lg"
								onClick={() => setIsTrimming?.(false)}
							>
								Cancel
							</Button>
						</HStack>
					</VStack>
				</Box>
			</Flex>
		);
	}

	if (!markupItem) return (
		<Flex 
			flexDir="column" 
			w="full" 
			h="full" 
			bg={useColorModeValue("white", "gray.900")} 
			borderLeft="1px solid" 
			borderColor={useColorModeValue("gray.200", "gray.800")}
		>
			{/* Clean, Modern Header */}
			<Flex justify="space-between" align="center" px={3.5} py={2.5} borderBottom="1px solid" borderColor={cardBorder} bg={cardBg}>
				<HStack spacing={2}>
					<FontAwesomeIcon icon={faLayerGroup} style={{ color: "#10b981", fontSize: "14px" }} />
					<Heading size="xs" textTransform="uppercase" letterSpacing="wider">Tags & Layers</Heading>
					<Badge colorScheme="teal" variant="subtle" borderRadius="full" px={2} py={0.5} fontSize="2xs">
						{(content?.polygons?.length || 0) + (content?.hotSpots?.length || 0)}
					</Badge>
				</HStack>
				<HStack spacing={1.5}>
					<Tooltip label={hasValidClip ? `Active Trim: ${(content.clipEnd! - content.clipStart!).toFixed(1)}s` : "Trim Chapter"} placement="top" hasArrow>
						<Button
							size="2xs"
							variant={hasValidClip ? "solid" : "outline"}
							colorScheme="teal"
							borderRadius="full"
							px={2}
							py={1}
							fontSize="10px"
							leftIcon={<FontAwesomeIcon icon={faScissors} />}
							onClick={() => setIsTrimming?.(true)}
						>
							{hasValidClip ? "Trimmed" : "Trim"}
						</Button>
					</Tooltip>
					{onToggleExpand && (
						<Tooltip label={isExpanded ? "Standard Width" : "Wider Inspector"} placement="top" hasArrow>
							<IconButton aria-label="Toggle width" size="xs" variant="ghost" icon={<FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />} onClick={onToggleExpand} />
						</Tooltip>
					)}
					{onToggleCollapse && (
						<Tooltip label="Collapse to Full Video" placement="top" hasArrow>
							<IconButton aria-label="Collapse panel" size="xs" variant="ghost" icon={<FontAwesomeIcon icon={faChevronRight} />} onClick={onToggleCollapse} />
						</Tooltip>
					)}
				</HStack>
			</Flex>

			{/* Quick Add Annotation Tools */}
			<Box px={3} py={2} borderBottom="1px solid" borderColor={cardBorder} bg={useColorModeValue("gray.50", "gray.850")}>
				<Flex justify="space-between" align="center" mb={1.5}>
					<Text fontSize="10px" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
						Add Annotation
					</Text>
					{tool && tool !== 'pointer' && (
						<Badge size="xs" colorScheme="green" variant="solid" borderRadius="full" fontSize="9px" px={2}>
							Drawing: {tool}
						</Badge>
					)}
				</Flex>
				<HStack spacing={1.5}>
					<Button
						size="xs"
						fontSize="xs"
						variant={tool === 'polygon' ? 'solid' : 'outline'}
						colorScheme={tool === 'polygon' ? 'green' : 'gray'}
						leftIcon={<FontAwesomeIcon icon={faDrawPolygon} />}
						onClick={() => setTool?.(tool === 'polygon' ? 'pointer' : 'polygon')}
						borderRadius="md"
						flex={1}
						h="28px"
					>
						Polygon
					</Button>
					<Button
						size="xs"
						fontSize="xs"
						variant={tool === 'hotspot' ? 'solid' : 'outline'}
						colorScheme={tool === 'hotspot' ? 'green' : 'gray'}
						leftIcon={<FontAwesomeIcon icon={faMapPin} />}
						onClick={() => setTool?.(tool === 'hotspot' ? 'pointer' : 'hotspot')}
						borderRadius="md"
						flex={1}
						h="28px"
					>
						Hotspot
					</Button>
					<Button
						size="xs"
						fontSize="xs"
						variant={tool === 'text' ? 'solid' : 'outline'}
						colorScheme={tool === 'text' ? 'green' : 'gray'}
						leftIcon={<FontAwesomeIcon icon={faFont} />}
						onClick={() => setTool?.(tool === 'text' ? 'pointer' : 'text')}
						borderRadius="md"
						flex={1}
						h="28px"
					>
						Text
					</Button>
					<Menu isLazy>
						<MenuButton
							as={Button}
							size="xs"
							fontSize="xs"
							variant={['rectangle', 'circle', 'triangle', 'arrow', 'line'].includes(tool || '') ? 'solid' : 'outline'}
							colorScheme={['rectangle', 'circle', 'triangle', 'arrow', 'line'].includes(tool || '') ? 'green' : 'gray'}
							borderRadius="md"
							h="28px"
							px={2}
						>
							+ Shape
						</MenuButton>
						<MenuList minW="140px" fontSize="xs" zIndex={100} shadow="lg" borderRadius="xl">
							<MenuItem onClick={() => setTool?.('rectangle')}>Rectangle Box</MenuItem>
							<MenuItem onClick={() => setTool?.('circle')}>Circle Shape</MenuItem>
							<MenuItem onClick={() => setTool?.('triangle')}>Triangle</MenuItem>
							<MenuItem onClick={() => setTool?.('arrow')}>Directional Arrow</MenuItem>
							<MenuItem onClick={() => setTool?.('line')}>Straight Line</MenuItem>
						</MenuList>
					</Menu>
				</HStack>
			</Box>

			{/* Interactive Tags & Layers List (Full Height) */}
			<Box flex={1} overflowY="auto" p={2.5}>
				{(!content?.polygons?.length && !content?.hotSpots?.length) ? (
					<Center py={12} flexDir="column" opacity={0.6}>
						<FontAwesomeIcon icon={faDrawPolygon} style={{ fontSize: "28px", marginBottom: "10px", color: "#10b981" }} />
						<Text fontSize="xs" fontWeight="semibold">No tags created yet</Text>
						<Text fontSize="2xs" color="gray.400" mt={1} textAlign="center" px={4}>
							Use the tools above to draw shapes or place hotspots on the video.
						</Text>
					</Center>
				) : (
					<VStack spacing={1.5} align="stretch">
						{content?.polygons?.map((poly, idx) => {
							const isSelected = content?.selectedMarkup?.type === 'polygon' && content?.selectedMarkup?.index === idx;
							const tagType = (poly as any).isText ? 'Text' : (poly as any).isCircle ? 'Circle' : 'Polygon';
							return (
								<Flex
									key={`poly-${idx}`}
									p={2}
									px={2.5}
									borderRadius="lg"
									bg={cardBg}
									border="1px solid"
									borderColor={isSelected ? "teal.400" : cardBorder}
									boxShadow={isSelected ? "0 0 0 1px rgba(20, 184, 166, 0.4)" : "none"}
									align="center"
									justify="space-between"
									cursor="pointer"
									_hover={{ borderColor: "teal.400", bg: useColorModeValue("gray.50", "gray.800"), transform: "translateY(-1px)" }}
									onClick={() => {
										if (onSelectMarkup) onSelectMarkup('polygon', idx);
										else setContent({ ...content, selectedMarkup: { type: 'polygon', index: idx, subIndex: -1 } });
										onSeekTime?.(poly.start);
									}}
									transition="all 0.12s ease"
								>
									<HStack spacing={2.5} minW={0} flex={1}>
										<Box w="9px" h="9px" borderRadius="full" bg={poly.color || "blue.500"} flexShrink={0} />
										<Box minW={0} flex={1}>
											<Text fontSize="xs" fontWeight="semibold" noOfLines={1} color={useColorModeValue("gray.800", "white")}>
												{poly.title || `${tagType} #${idx + 1}`}
											</Text>
											<HStack spacing={1.5} mt={0.5}>
												<Text fontSize="10px" color="gray.400">
													{poly.start.toFixed(1)}s – {poly.end.toFixed(1)}s
												</Text>
												<Badge size="xs" variant="subtle" colorScheme="purple" fontSize="9px" px={1.5} borderRadius="sm">
													{tagType}
												</Badge>
											</HStack>
										</Box>
									</HStack>
									{onDeleteMarkup && (
										<IconButton
											aria-label="Delete tag"
											size="xs"
											variant="ghost"
											colorScheme="red"
											icon={<FontAwesomeIcon icon={faTrash} style={{ fontSize: "11px" }} />}
											onClick={(e) => {
												e.stopPropagation();
												onDeleteMarkup('polygon', idx);
											}}
											opacity={0.5}
											_hover={{ opacity: 1, bg: "red.50", _dark: { bg: "red.900" } }}
										/>
									)}
								</Flex>
							);
						})}
						{content?.hotSpots?.map((spot, idx) => {
							const isSelected = content?.selectedMarkup?.type === 'hotspot' && content?.selectedMarkup?.index === idx;
							const startTime = spot.timeStamps?.[0] ?? 0;
							const endTime = spot.timeStamps?.[spot.timeStamps.length - 1] ?? startTime;
							return (
								<Flex
									key={`spot-${idx}`}
									p={2}
									px={2.5}
									borderRadius="lg"
									bg={cardBg}
									border="1px solid"
									borderColor={isSelected ? "teal.400" : cardBorder}
									boxShadow={isSelected ? "0 0 0 1px rgba(20, 184, 166, 0.4)" : "none"}
									align="center"
									justify="space-between"
									cursor="pointer"
									_hover={{ borderColor: "teal.400", bg: useColorModeValue("gray.50", "gray.800"), transform: "translateY(-1px)" }}
									onClick={() => {
										if (onSelectMarkup) onSelectMarkup('hotspot', idx);
										else setContent({ ...content, selectedMarkup: { type: 'hotspot', index: idx, subIndex: -1 } });
										onSeekTime?.(startTime);
									}}
									transition="all 0.12s ease"
								>
									<HStack spacing={2.5} minW={0} flex={1}>
										<Box w="9px" h="9px" borderRadius="full" bg="teal.500" flexShrink={0} />
										<Box minW={0} flex={1}>
											<Text fontSize="xs" fontWeight="semibold" noOfLines={1} color={useColorModeValue("gray.800", "white")}>
												{spot.title || `Hotspot #${idx + 1}`}
											</Text>
											<HStack spacing={1.5} mt={0.5}>
												<Text fontSize="10px" color="gray.400">
													{startTime.toFixed(1)}s – {endTime.toFixed(1)}s
												</Text>
												<Badge size="xs" variant="subtle" colorScheme="teal" fontSize="9px" px={1.5} borderRadius="sm">
													Hotspot
												</Badge>
											</HStack>
										</Box>
									</HStack>
									{onDeleteMarkup && (
										<IconButton
											aria-label="Delete tag"
											size="xs"
											variant="ghost"
											colorScheme="red"
											icon={<FontAwesomeIcon icon={faTrash} style={{ fontSize: "11px" }} />}
											onClick={(e) => {
												e.stopPropagation();
												onDeleteMarkup('hotspot', idx);
											}}
											opacity={0.5}
											_hover={{ opacity: 1, bg: "red.50", _dark: { bg: "red.900" } }}
										/>
									)}
								</Flex>
							);
						})}
					</VStack>
				)}
			</Box>
		</Flex>
	);

	const isPolygon = Boolean((markupItem as EditPolygon).points);
	const isTextTag = Boolean((markupItem as EditPolygon).isText);
	const isCircleTag = Boolean((markupItem as EditPolygon).isCircle);
	const isHotspot = !isPolygon;

	const handleSliderRotationChange = (newVal: number) => {
		const poly = markupItem as EditPolygon;
		if (!poly.points || poly.points.length === 0) return;

		if (poly.isCircle) {
			const { cX, cY, rx, ry } = getCircleEllipseParams(poly);
			const newPoints = generateCircleEllipsePoints(cX, cY, rx, ry, newVal);
			setMarkupItem(generateMidpoints({
				...poly,
				points: newPoints,
				rotation: newVal
			} as EditPolygon));
			return;
		}

		const currentRot = poly.rotation || 0;
		const deltaDegrees = newVal - currentRot;
		const deltaRadians = (deltaDegrees * Math.PI) / 180;
		
		const { points } = poly;
		const cX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
		const cY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
		
		const cos = Math.cos(deltaRadians);
		const sin = Math.sin(deltaRadians);
		
		const newPoints = points.map(p => {
			const dx = p.x - cX;
			const dy = p.y - cY;
			return {
				x: Math.round(cX + dx * cos - dy * sin),
				y: Math.round(cY + dx * sin + dy * cos)
			};
		});
		
		setMarkupItem(generateMidpoints({
			...markupItem,
			points: newPoints,
			rotation: newVal
		} as EditPolygon));
	};

	return (
		<Box 
			w="full" 
			h="full" 
			bg={useColorModeValue("gray.50", "gray.900")}
			borderLeft="1px solid" 
			borderColor={useColorModeValue("gray.200", "gray.800")}
			display="flex"
			flexDir="column"
			overflow="hidden"
		>
			{/* Panel Header */}
			<Flex 
				justify="space-between" 
				align="center" 
				px={3.5}
				py={2} 
				borderBottom="1px solid" 
				borderColor={useColorModeValue("gray.200", "gray.750")}
				bg={useColorModeValue("white", "gray.850")}
				boxShadow="xs"
			>
				<HStack spacing={2}>
					<Box
						w="22px"
						h="22px"
						borderRadius="md"
						bg={`${markupItem.color}25`}
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<FontAwesomeIcon
							icon={isTextTag ? faFont : isHotspot ? faMapPin : faDrawPolygon}
							style={{ fontSize: '11px', color: markupItem.color }}
						/>
					</Box>
					<Heading size="xs" textTransform="uppercase" color={sectionTitleColor} letterSpacing="wider">
						Tag Properties
					</Heading>
				</HStack>

				<HStack spacing={1.5}>
					{onDelete && (
						<Tooltip label="Delete Tag" placement="top" hasArrow>
							<IconButton
								aria-label="Delete Tag"
								icon={<FontAwesomeIcon icon={faTrash} />}
								size="xs"
								colorScheme="red"
								variant="ghost"
								borderRadius="lg"
								onClick={onDelete}
							/>
						</Tooltip>
					)}
					<Button
						size="xs"
						colorScheme="green"
						bg="#10b981"
						color="white"
						_hover={{ bg: "#059669" }}
						onClick={onDone}
						borderRadius="full"
						px={3.5}
						py={1}
						boxShadow="sm"
					>
						Done
					</Button>
					{onToggleExpand && (
						<IconButton
							aria-label={isExpanded ? "Standard width" : "Expand width"}
							icon={<FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />}
							size="xs"
							variant="ghost"
							borderRadius="lg"
							onClick={onToggleExpand}
						/>
					)}
					{onToggleCollapse && (
						<Tooltip label="Collapse to Full Video" placement="top" hasArrow>
							<IconButton
								aria-label="Collapse panel"
								icon={<FontAwesomeIcon icon={faChevronRight} />}
								size="xs"
								variant="ghost"
								borderRadius="lg"
								onClick={onToggleCollapse}
							/>
						</Tooltip>
					)}
				</HStack>
			</Flex>

			{/* Scrollable Form Body */}
			<Box
				flex={1}
				overflowY="auto"
				p={3}
				display="flex"
				flexDir="column"
				gap={3}
				css={{
					"&::-webkit-scrollbar": { width: "4px" },
					"&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.15)", borderRadius: "9999px" },
				}}
			>
				{/* 1. Title & Clinical Description Card */}
				<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
					<VStack spacing={3} align="stretch">
						<Box>
							<Flex align="center" gap={1.5} mb={1}>
								<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
									Tag Title
								</Text>
								<Tooltip label="The display name of this anatomical structure or tag." placement="top" hasArrow>
									<Box as="span" cursor="help" color="gray.400">
										<FontAwesomeIcon icon={faInfoCircle} size="xs" />
									</Box>
								</Tooltip>
							</Flex>
							<Input 
								size="sm"
								borderRadius="lg"
								bg={useColorModeValue("gray.50", "gray.800")}
								border="1px solid"
								borderColor={useColorModeValue("gray.200", "gray.700")}
								_focus={{ borderColor: "#10b981", bg: useColorModeValue("white", "gray.900"), boxShadow: "0 0 0 1px #10b981" }}
								value={markupItem.title} 
								onChange={(e) => setMarkupItem({ ...markupItem, title: e.target.value })} 
								placeholder="e.g. Medial Meniscus" 
								fontWeight="600"
							/>
						</Box>

						<Box>
							<Flex align="center" gap={1.5} mb={1}>
								<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
									Clinical Description
								</Text>
								<Tooltip label="Detailed anatomical or clinical notes displayed in the info overlay during playback." placement="top" hasArrow>
									<Box as="span" cursor="help" color="gray.400">
										<FontAwesomeIcon icon={faInfoCircle} size="xs" />
									</Box>
								</Tooltip>
							</Flex>
							<Textarea 
								size="sm"
								borderRadius="lg"
								bg={useColorModeValue("gray.50", "gray.800")}
								border="1px solid"
								borderColor={useColorModeValue("gray.200", "gray.700")}
								_focus={{ borderColor: "#10b981", bg: useColorModeValue("white", "gray.900"), boxShadow: "0 0 0 1px #10b981" }}
								value={markupItem.description} 
								onChange={(e) => setMarkupItem({ ...markupItem, description: e.target.value })} 
								placeholder="Add clinical or anatomical notes..."
								rows={2}
								fontSize="xs"
							/>
						</Box>
					</VStack>
				</Box>

				{/* 2. Timing Card */}
				{(markupItem as EditPolygon).midPoints && (
					<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
						<Flex align="center" justify="space-between" mb={2}>
							<HStack spacing={1.5}>
								<FontAwesomeIcon icon={faClock} style={{ fontSize: '11px', color: '#10b981' }} />
								<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
									Visibility Timing
								</Text>
							</HStack>
							<Badge colorScheme="green" variant="subtle" borderRadius="md" fontSize="2xs" px={1.5}>
								Duration: {Math.max(0, Math.round(((markupItem as EditPolygon).end - (markupItem as EditPolygon).start) * 10) / 10).toFixed(1)}s
							</Badge>
						</Flex>
						<Flex gap={2}>
							<InputGroup size="sm" flex={1}>
								<InputLeftAddon borderLeftRadius="lg" px={2} fontSize="2xs" fontWeight="700">Start</InputLeftAddon>
								<Input 
									type='number' 
									borderRightRadius="lg"
									bg={useColorModeValue("gray.50", "gray.800")}
									value={(markupItem as EditPolygon).start} 
									onChange={(e) => setMarkupItem({ ...markupItem, start: parseFloat(e.target.value) || 0 })} 
									fontWeight="600"
								/>
							</InputGroup>
							<InputGroup size="sm" flex={1}>
								<InputLeftAddon borderLeftRadius="lg" px={2} fontSize="2xs" fontWeight="700">End</InputLeftAddon>
								<Input 
									type='number' 
									borderRightRadius="lg"
									bg={useColorModeValue("gray.50", "gray.800")}
									value={(markupItem as EditPolygon).end} 
									onChange={(e) => setMarkupItem({ ...markupItem, end: parseFloat(e.target.value) || 0 })} 
									fontWeight="600"
								/>
							</InputGroup>
						</Flex>
					</Box>
				)}

				{/* 3. Title Placement & Alignment Card (Hidden for Text Tags) */}
				{!isTextTag && isPolygon && markupItem.title && (
					<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
						<Flex align="center" gap={1.5} mb={2}>
							<FontAwesomeIcon icon={faArrowsUpDownLeftRight} style={{ fontSize: '11px', color: '#3b82f6' }} />
							<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
								Title Placement & Position
							</Text>
						</Flex>

						{/* Modern Segmented 5-Way Buttons */}
						<Flex bg={useColorModeValue("gray.100", "gray.800")} p={1} borderRadius="xl" gap={1} mb={3}>
							{[
								{ id: 'top', label: 'Top' },
								{ id: 'bottom', label: 'Bottom' },
								{ id: 'left', label: 'Left' },
								{ id: 'right', label: 'Right' },
								{ id: 'center', label: 'Center' },
							].map(pos => {
								const isSelected = ((markupItem as EditPolygon).titlePosition || 'top') === pos.id;
								return (
									<Button
										key={pos.id}
										size="xs"
										variant={isSelected ? "solid" : "ghost"}
										colorScheme={isSelected ? "blue" : "gray"}
										bg={isSelected ? "blue.500" : "transparent"}
										color={isSelected ? "white" : useColorModeValue("gray.600", "gray.400")}
										onClick={() => setMarkupItem({ ...markupItem, titlePosition: pos.id as any } as EditPolygon)}
										borderRadius="lg"
										flex={1}
										fontSize="10px"
										fontWeight="700"
										py={1.5}
										boxShadow={isSelected ? "0 2px 6px rgba(59, 130, 246, 0.4)" : "none"}
										transition="all 0.15s ease"
									>
										{pos.label}
									</Button>
								);
							})}
						</Flex>

						{/* Offset Distance Slider (only if not center) */}
						{((markupItem as EditPolygon).titlePosition || 'top') !== 'center' && (
							<Box mb={3}>
								<Flex align="center" justify="space-between" mb={1.5}>
									<Text fontSize="2xs" color={labelColor} fontWeight="700" textTransform="uppercase">
										Distance Offset:
									</Text>
									<Badge colorScheme="blue" variant="subtle" borderRadius="md" fontSize="2xs" px={1.5}>
										{(markupItem as EditPolygon).titleOffset !== undefined ? (markupItem as EditPolygon).titleOffset : 15}px
									</Badge>
								</Flex>
								<Flex align="center" gap={3} bg={useColorModeValue("gray.50", "gray.800")} py={2} px={3} borderRadius="xl" border="1px solid" borderColor={cardBorder}>
									<Slider
										min={0}
										max={60}
										step={2}
										value={(markupItem as EditPolygon).titleOffset !== undefined ? (markupItem as EditPolygon).titleOffset : 15}
										onChange={(val) => setMarkupItem({ ...markupItem, titleOffset: val } as EditPolygon)}
										colorScheme="blue"
										flex={1}
									>
										<SliderTrack h="5px" borderRadius="full">
											<SliderFilledTrack bg="blue.500" />
										</SliderTrack>
										<SliderThumb boxSize={3.5} bg="white" boxShadow="md" borderColor="blue.500" borderWidth="2px" />
									</Slider>
								</Flex>
							</Box>
						)}

						{/* Modern Checkboxes */}
						<VStack spacing={2} align="stretch" pt={1}>
							<Checkbox 
								isChecked={(markupItem as EditPolygon).showLeaderLine !== false} 
								onChange={(e) => setMarkupItem({ ...markupItem, showLeaderLine: e.target.checked } as EditPolygon)}
								colorScheme="blue"
								fontWeight="600"
								fontSize="xs"
								spacing={2.5}
							>
								Show Connector Line
							</Checkbox>
							<Checkbox 
								isChecked={(markupItem as EditPolygon).keepAspectRatio !== false} 
								onChange={(e) => setMarkupItem({ ...markupItem, keepAspectRatio: e.target.checked } as EditPolygon)}
								colorScheme="blue"
								fontWeight="600"
								fontSize="xs"
								spacing={2.5}
							>
								Lock Aspect Ratio
							</Checkbox>
						</VStack>
					</Box>
				)}

				{/* 4. Color & Appearance Card */}
				<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
					<Flex align="center" gap={1.5} mb={2.5}>
						<FontAwesomeIcon icon={faPalette} style={{ fontSize: '11px', color: '#ec4899' }} />
						<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
							Color & Appearance
						</Text>
					</Flex>

					{/* Custom Hex + Color Swatches */}
					<VStack spacing={3} align="stretch">
						<Flex align="center" justify="space-between" bg={useColorModeValue("gray.50", "gray.800")} p={2.5} borderRadius="xl" border="1px solid" borderColor={cardBorder}>
							<HStack spacing={2}>
								<Box
									position="relative"
									w="28px"
									h="28px"
									borderRadius="lg"
									overflow="hidden"
									border="2px solid white"
									boxShadow="0 0 0 1px rgba(0,0,0,0.15)"
									bg={markupItem.color}
								>
									<Input 
										type='color' 
										value={markupItem.color} 
										onChange={(e) => setMarkupItem({ ...markupItem, color: e.target.value })} 
										position="absolute"
										top="-8px"
										left="-8px"
										w="46px"
										h="46px"
										p={0}
										border="none"
										cursor="pointer"
									/>
								</Box>
								<VStack spacing={0} align="start">
									<Text fontSize="2xs" color={labelColor} fontWeight="700">Custom Color</Text>
									<Text fontSize="xs" color={useColorModeValue("gray.700", "gray.200")} fontFamily="mono" fontWeight="bold">
										{markupItem.color.toUpperCase()}
									</Text>
								</VStack>
							</HStack>
						</Flex>

						{/* Preset Swatches Palette */}
						<Box>
							<Text fontSize="2xs" color={labelColor} fontWeight="700" textTransform="uppercase" mb={2}>
								Curated Medical Palette:
							</Text>
							<Flex gap={2} flexWrap="wrap">
								{PRESET_COLORS.map(c => {
									const isSelected = markupItem.color.toLowerCase() === c.hex.toLowerCase();
									return (
										<Tooltip key={c.hex} label={c.label} placement="top" hasArrow>
											<Box
												w="26px"
												h="26px"
												borderRadius="lg"
												bg={c.hex}
												cursor="pointer"
												display="flex"
												alignItems="center"
												justifyContent="center"
												boxShadow={isSelected ? `0 0 0 2px ${useColorModeValue("white", "black")}, 0 0 0 4px ${c.hex}` : "0 2px 4px rgba(0,0,0,0.15)"}
												_hover={{ transform: 'scale(1.15)', boxShadow: "0 4px 8px rgba(0,0,0,0.25)" }}
												transition="all 0.15s ease"
												onClick={() => setMarkupItem({ ...markupItem, color: c.hex })}
											>
												{isSelected && <FontAwesomeIcon icon={faCheck} style={{ color: 'white', fontSize: '10px' }} />}
											</Box>
										</Tooltip>
									);
								})}
							</Flex>
						</Box>

						{isPolygon && (
							<Checkbox 
								isChecked={!(markupItem as EditPolygon).noFill} 
								onChange={(e) => setMarkupItem({ ...markupItem, noFill: !e.target.checked } as EditPolygon)}
								colorScheme="green"
								fontWeight="600"
								fontSize="xs"
								spacing={2.5}
							>
								Fill Shape with Color
							</Checkbox>
						)}
					</VStack>
				</Box>

				{/* 5. Line Thickness Card (for Polygons/Shapes) */}
				{isPolygon && (
					<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
						<Flex align="center" justify="space-between" mb={2}>
							<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
								Line Thickness
							</Text>
							<Badge colorScheme="blue" variant="subtle" borderRadius="md" fontSize="2xs" px={1.5}>
								{(markupItem as EditPolygon).thickness || 2}px
							</Badge>
						</Flex>
						<Flex align="center" gap={3} bg={useColorModeValue("gray.50", "gray.800")} py={2} px={3} borderRadius="xl" border="1px solid" borderColor={cardBorder} mb={2.5}>
							<Slider
								min={1}
								max={16}
								step={1}
								value={(markupItem as EditPolygon).thickness || 2}
								onChange={(val) => setMarkupItem({ ...markupItem, thickness: val } as EditPolygon)}
								colorScheme="blue"
								flex={1}
							>
								<SliderTrack h="5px" borderRadius="full">
									<SliderFilledTrack bg="blue.500" />
								</SliderTrack>
								<SliderThumb boxSize={3.5} bg="white" boxShadow="md" borderColor="blue.500" borderWidth="2px" />
							</Slider>
						</Flex>
						<Flex gap={1.5}>
							{[1, 2, 3, 5, 8].map(px => (
								<Button
									key={px}
									size="xs"
									variant={((markupItem as EditPolygon).thickness || 2) === px ? "solid" : "outline"}
									colorScheme="blue"
									onClick={() => setMarkupItem({ ...markupItem, thickness: px } as EditPolygon)}
									borderRadius="lg"
									flex={1}
									fontSize="10px"
									fontWeight="700"
								>
									{px}px
								</Button>
							))}
						</Flex>
					</Box>
				)}

				{/* 6. Rotation Section (if Polygon / Shape) */}
				{isPolygon && (
					<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
						<Flex align="center" justify="space-between" mb={2}>
							<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
								Shape Rotation
							</Text>
							<Badge colorScheme="blue" variant="subtle" borderRadius="md" fontSize="2xs" px={1.5}>
								{Math.round((markupItem as EditPolygon).rotation || 0)}°
							</Badge>
						</Flex>
						<Flex align="center" gap={3} bg={useColorModeValue("gray.50", "gray.800")} py={2} px={3} borderRadius="xl" border="1px solid" borderColor={cardBorder} mb={2.5}>
							<Slider
								min={0}
								max={360}
								step={5}
								value={(markupItem as EditPolygon).rotation || 0}
								onChange={handleSliderRotationChange}
								colorScheme="blue"
								flex={1}
							>
								<SliderTrack h="5px" borderRadius="full">
									<SliderFilledTrack bg="blue.500" />
								</SliderTrack>
								<SliderThumb boxSize={3.5} bg="white" boxShadow="md" borderColor="blue.500" borderWidth="2px" />
							</Slider>
						</Flex>
						<Flex gap={1.5}>
							{[0, 90, 180, 270].map(deg => (
								<Button
									key={deg}
									size="xs"
									variant={Math.round((markupItem as EditPolygon).rotation || 0) === deg ? "solid" : "outline"}
									colorScheme="blue"
									onClick={() => handleSliderRotationChange(deg)}
									borderRadius="lg"
									flex={1}
									fontSize="10px"
									fontWeight="700"
								>
									{deg}°
								</Button>
							))}
						</Flex>
					</Box>
				)}

				{/* 7. Circle Size / Dimensions (if Circle) */}
				{isCircleTag && isPolygon && (
					<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
						<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider" mb={2.5}>
							Circle Size & Dimensions
						</Text>
						<Flex gap={2} mb={2.5}>
							<InputGroup size="sm">
								<InputLeftAddon borderLeftRadius="lg" fontSize="2xs" fontWeight="700">Radius (r)</InputLeftAddon>
								<Input 
									type='number' 
									borderRightRadius="lg"
									bg={useColorModeValue("gray.50", "gray.800")}
									value={Math.round(getCircleEllipseParams(markupItem as EditPolygon).rx)} 
									onChange={(e) => {
										const r = Math.max(5, parseFloat(e.target.value) || 10);
										const poly = markupItem as EditPolygon;
										const { cX, cY, rx, ry, isCircleType } = getCircleEllipseParams(poly);
										const scale = r / Math.max(1, rx);
										const newRx = Math.max(5, Math.round(rx * scale));
										const newRy = isCircleType ? newRx : Math.max(5, Math.round(ry * scale));
										const newPoints = generateCircleEllipsePoints(cX, cY, newRx, newRy, poly.rotation || 0);
										setMarkupItem(generateMidpoints({
											...markupItem,
											points: newPoints
										} as EditPolygon));
									}} 
									fontWeight="600"
								/>
							</InputGroup>
						</Flex>
						<Checkbox 
							isChecked={Boolean((markupItem as EditPolygon).keepAspectRatio)} 
							onChange={(e) => setMarkupItem({ ...markupItem, keepAspectRatio: e.target.checked } as EditPolygon)}
							colorScheme="blue"
							fontWeight="600"
							fontSize="xs"
						>
							Lock 1:1 Aspect Ratio (Perfect Circle)
						</Checkbox>
					</Box>
				)}

				{/* 8. Audio Files Card */}
				<Box bg={cardBg} p={3.5} borderRadius="xl" border="1px solid" borderColor={cardBorder} boxShadow="xs">
					<Flex align="center" justify="space-between" mb={2}>
						<HStack spacing={1.5}>
							<FontAwesomeIcon icon={faVolumeHigh} style={{ fontSize: '11px', color: '#10b981' }} />
							<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider">
								Audio Commentary
							</Text>
						</HStack>
					</Flex>
					<ListPropertyEditor
						header=""
						list={markupItem.audios}
						RenderComponent={AudioFileItem}
						AddNewButton={
							<Menu>
								<MenuButton as={Button} rightIcon={<FontAwesomeIcon icon={faPlus} />} size="xs" colorScheme="blue" variant="outline" borderRadius="lg" w="full">
									Attach Audio File
								</MenuButton>
								<Portal>
									<MenuList maxH="250px" overflowY="auto" zIndex={1600} borderRadius="xl" shadow="xl" p={2} bg={useColorModeValue("white", "gray.850")}>
										{allAudios.length === 0 ? (
											<MenuItem isDisabled fontSize="xs">No audio files in Media Manager</MenuItem>
										) : (
											allAudios.map(audio => (
												<MenuItem key={audio.id} borderRadius="md" fontSize="xs" onClick={() => {
													setMarkupItem({
														...markupItem,
														audios: [...(markupItem.audios || []), { name: audio.name, id: audio.id }]
													});
													setMedia(mediaMeta =>
														mediaMeta!.map(meta => {
															const refId = selectionReference?.id;
															if (!refId) return meta;
															let updatedRefs = meta.references;
															if (!meta.references.includes(refId)) {
																updatedRefs = [...meta.references, refId];
															}
															return {
																...meta,
																references: updatedRefs
															};
														})
													);
												}}>{audio.name}</MenuItem>
											))
										)}
									</MenuList>
								</Portal>
							</Menu>
						}
						onUpdate={(updatedList) => {
							const oldList = markupItem.audios || [];
							const removed = oldList.filter(oldItem => !updatedList.some(newItem => newItem.id === oldItem.id));
							setMarkupItem({
								...markupItem,
								audios: updatedList
							});
							setMedia(mediaMeta =>
								mediaMeta!.map(meta => {
									const refId = selectionReference?.id;
									if (!refId) return meta;
									let updatedRefs = meta.references;
									if (removed.length > 0 && meta?.id === removed[removed.length - 1]?.id) {
										updatedRefs = meta.references.filter(ref => ref !== refId);
									}
									return {
										...meta,
										references: [...new Set(updatedRefs)]
									};
								})
							);
						}}
					/>
				</Box>

				{/* 9. Advanced Section (Points & Hidden Frames) */}
				<Accordion allowToggle>
					<AccordionItem border="none">
						<AccordionButton p={2.5} _hover={{ bg: useColorModeValue("gray.100", "gray.800") }} borderRadius="xl" border="1px solid" borderColor={cardBorder} bg={cardBg}>
							<Flex justify="space-between" align="center" w="100%">
								<Heading size="2xs" textTransform="uppercase" color={labelColor} letterSpacing="wider">
									Advanced Geometry & Hidden Frames
								</Heading>
								<AccordionIcon />
							</Flex>
						</AccordionButton>
						<AccordionPanel p={0} pt={3}>
							<VStack spacing={3} align="stretch">
								{/* Hidden Frames */}
								<Box bg={cardBg} p={3} borderRadius="xl" border="1px solid" borderColor={cardBorder}>
									<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider" mb={2}>
										Hidden Frames
									</Text>
									<ListPropertyEditor
										header=""
										list={markupItem.hiddenFrames as any[]}
										RenderComponent={HiddenFrameInput}
										AddNewButton={<AddButton
											label="hidden frame"
											onAdd={() => setMarkupItem({
												...markupItem,
												hiddenFrames: [...(markupItem.hiddenFrames || []), Math.round(time * 10) / 10]
											} as any)}
										/>}
										onUpdate={(list) => {
											setMarkupItem({
												...markupItem,
												hiddenFrames: list
											} as any)
										}}
									/>
								</Box>

								{/* Points List */}
								{isPolygon && (
									<Box bg={cardBg} p={3} borderRadius="xl" border="1px solid" borderColor={cardBorder}>
										<Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={labelColor} letterSpacing="wider" mb={2}>
											Vertex Coordinates
										</Text>
										<ListPropertyEditor
											header=''
											list={(markupItem as EditPolygon).points}
											RenderComponent={CoordinateInput}
											AddNewButton={
												<AddButton
													label="vertex point"
													onAdd={() => setMarkupItem({
														...markupItem,
														points: [...(markupItem as EditPolygon).points, { x: 0, y: 0 }]
													})}
												/>}
											onUpdate={(list) => {
												setMarkupItem(generateMidpoints({
													...markupItem,
													points: list
												} as EditPolygon))
											}}
										/>
									</Box>
								)}
							</VStack>
						</AccordionPanel>
					</AccordionItem>
				</Accordion>

				{/* Bottom Save & Delete Actions */}
				<Box mt={2} pt={2} display="flex" gap={2}>
					{onDelete && (
						<Button
							colorScheme="red"
							variant="outline"
							size="sm"
							leftIcon={<FontAwesomeIcon icon={faTrash} />}
							onClick={onDelete}
							borderRadius="xl"
							fontSize="xs"
							fontWeight="700"
						>
							Delete
						</Button>
					)}
					<Button
						colorScheme="green"
						bg="#10b981"
						color="white"
						_hover={{ bg: "#059669" }}
						size="sm"
						flex={1}
						onClick={onDone}
						borderRadius="xl"
						fontSize="xs"
						fontWeight="700"
						boxShadow="0 2px 8px rgba(16, 185, 129, 0.35)"
					>
						Save & Close
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

export default MarkupPropertyEditor;
